import { useState } from "owa-react";
import { addMailListLog, getMailListLogObjectToAddToStore } from "owa-mail-list-logging";
import { FilterFilled, FilterRegular, bundleIcon } from "@fluentui/react-icons";
import { getDensityModeCssClass } from "owa-fabric-theme";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import { getPalette } from "owa-theme";
import { getStore as getListViewStore, getViewFilterForTable } from "owa-mail-list-store";
import { getViewFilterIcon } from "../helpers/getViewFilterIcon";
import { isSingleLineListView, shouldShowListView, useIconFillOnHover } from "owa-mail-layout";
import { lazyRemoveFilterOverrideAndReloadFolder, lazySelectFilter } from "owa-mail-filter-actions";
import { logUsage } from "owa-analytics";
import { mailFilter, sortBySize } from "../strings.locstring.json";
import { mergeStyles } from "@fluentui/merge-styles";
import { observer } from "owa-mobx-react";
import { resetFocus } from "owa-mail-focus-manager";
import classnames from "owa-classnames";
import getViewFilterDisplay from "../helpers/getViewFilterDisplay";
import loc from "owa-localize";
import React from "react";
import type { ActionSource } from "owa-mail-store";
import type { MailFolderTableQuery } from "owa-mail-list-store";
import type { MenuProps, MenuOpenChangeData, PositioningProps } from "@fluentui/react-components";
import type ViewFilter from "owa-service/lib/contract/ViewFilter";
import { makeStyles, mergeClasses, Menu, MenuButton, MenuItemRadio, MenuList, MenuPopover, MenuTrigger, Tooltip, } from "@fluentui/react-components";
import { filterButton, filterButtonCompact, filterButtonCompactMCL, filterButtonFull, filterButtonFullMCL, filterButtonMedium, filterButtonMediumMCL, filterButtonOpened, filterMenuItemLabelFull, filterMenuItemLabelMediumOrCompact, viewFilterMenuOption, bleedThroughBackground, filterButtonDenseless, filterButtonDenselessIconOnly, } from "./MailListFilterMenu.scss";
import { shouldShowViewFilterForMailbox } from "../helpers/shouldShowViewFilterForMailbox";
import { useComputedValue } from "owa-react-hooks/lib/useComputed";
import { isPrioritizeOnDeliveryOn } from "owa-copilot-settings-utils";
import { useIsTypeRampUpdatesEnabled } from "owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled";
import { logPrioritizeTelemetryEvent } from "owa-copilot-telemetry/lib/prioritizeTelemetryLogger";
import { getMailboxInfoFromTableViewId } from "owa-mail-mailboxinfo/lib/getMailboxInfo";

// Styles for the filter button icon that sits on top of the ML
// The !important bit is required to override default Fluent styling that is
// applied as a style directly to the icon element
const useFilterButtonIconStyles = makeStyles({
    full: {
        fontSize: "20px !important",
    },
    mediumOrCompact: {
        fontSize: "16px !important",
        marginTop: "2px",
    },
});

// Styles for the icons within the filter menu
// The !important bit is required to override default Fluent styling that is
// applied as a style directly to the icon element
const useMenuItemFilterIconStyles = makeStyles({
    icon: {
        fontSize: "20px !important",
        width: "20px",
    },
});

export interface MailListFilterMenuProps {
    filterButtonClassName?: string;
    filterMenuSource: ActionSource;
    supportedViewFilters: ViewFilter[];
    tableViewId: string;
    showButtonLabel?: boolean; // Making this optional because other components rely on only checking isSingleLineListView
    isBleedThrough?: boolean;
}

const FilterIcon = bundleIcon(FilterFilled, FilterRegular);

// Fit the menu to available window height and add a scroll (e.g. at high zoom).
const menuPositioning: PositioningProps = {
    autoSize: "height",
};

const defaultExport: React.FunctionComponent<MailListFilterMenuProps> = observer(function MailListFilterMenu(props: MailListFilterMenuProps) {
    const { filterButtonClassName, filterMenuSource, showButtonLabel = true, supportedViewFilters, tableViewId, isBleedThrough, } = props;
    
    const tableView = getListViewStore().tableViews.get(tableViewId);
    const mailboxInfo = getMailboxInfo(tableView);
    const currentSelectedFilter: ViewFilter | null = getViewFilterForTable(tableView);
    const mailFolderTableQuery = tableView?.tableQuery as MailFolderTableQuery | undefined;
    const isRPDenselessEnabled = useIsTypeRampUpdatesEnabled();
    
    const prioritizeOnDeliveryOn = isPrioritizeOnDeliveryOn(mailboxInfo);
    
    // The "open" state of the filter menu
    const [open, setOpen] = useState(false);
    const onOpenChange: MenuProps["onOpenChange"] = React.useCallback((_e: any, data: MenuOpenChangeData) => {
        if (!data.open) {
            setOpen(data.open);
        }
        
        if (data.open && prioritizeOnDeliveryOn) {
            logPrioritizeTelemetryEvent("OpenFilter", mailboxInfo);
        }
    }, [prioritizeOnDeliveryOn, mailboxInfo]);
    
    // The selected view filter values in the filter menu
    /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
     * Please remove the array or object from React.useState() or leave a justification in case is not possible to do so.
     *	> It is preferable not to use arrays or objects as react state, destruct the state into primitive data types or use satchel instead. */
    const [checkedValues, setCheckedValues] = useState<Record<string, string[]>>({
        viewFilter: ["All"],
    });
    const onCheckedValueChange = React.useCallback<Exclude<MenuProps["onCheckedValueChange"], undefined>>((_e, { name, checkedItems }) => {
        setCheckedValues(s => ({ ...s, [name]: checkedItems }));
    }, []);
    
    // Update the checked values when the selected view filter changes
    React.useEffect(() => {
        /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
         * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
         *	> Function 'setCheckedValues' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
        setCheckedValues(s => ({ ...s, viewFilter: [currentSelectedFilter || "All"] }));
    }, [currentSelectedFilter]);
    
    // Reset checked values when the tableView.id changes
    React.useEffect(() => {
        /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
         * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
         *	> Function 'setCheckedValues' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
        setCheckedValues(s => ({ ...s, viewFilter: ["All"] }));
    }, [tableViewId]);
    
    // Close the filter menu when the list view is hidden by responsive layout
    // (e.g. window resized small while reading pane is open). The MenuPopover
    // renders via a Fluent UI portal, so it stays visible even though the
    // ListPane container is hidden with visibility:hidden.
    const isListViewShown = shouldShowListView();
    
    const isUsingOverrideToSortBySize = React.useMemo(() => {
        return mailFolderTableQuery?.scenarioType === "mailFolderSortBySize";
    }, [mailFolderTableQuery?.scenarioType]);
    
    // The label to display for the filter button above the ML (if any)
    const showFilterButtonLabel = (showButtonLabel && isSingleLineListView()) || isUsingOverrideToSortBySize;
    const filterMenuLabel = React.useMemo(() => {
        if (isUsingOverrideToSortBySize) {
            return loc(sortBySize);
        }
        
        if (currentSelectedFilter == "All" /* isAllFilterSelected */) {
            return loc(mailFilter);
        }
        
        return currentSelectedFilter ? getViewFilterDisplay(currentSelectedFilter) : "";
    }, [currentSelectedFilter, isUsingOverrideToSortBySize]);
    
    const onFilterButtonClicked = React.useCallback((evt: React.MouseEvent<unknown> | React.KeyboardEvent<HTMLElement>) => {
        evt.stopPropagation();
        
        if (isUsingOverrideToSortBySize) {
            lazyRemoveFilterOverrideAndReloadFolder.importAndExecute(tableViewId);
            return;
        }
        
        // If the user clicks on the filter button while the "All" filter is selected,
        // then we want to dismiss the activated filter and reset the focus to the
        // mail list. Otherwise, we want to open the filter menu.
        const isAllFilterSelected = currentSelectedFilter === "All";
        if (!isAllFilterSelected) {
            logUsage("FilterMenuDismissActivatedFilter", getMailboxInfoFromTableViewId(tableViewId), {
                dismissedFilter: currentSelectedFilter,
            });
            
            lazySelectFilter.importAndExecute("All", filterMenuSource);
            addMailListLog(getMailListLogObjectToAddToStore("RemoveActivatedListViewFilter", {
                tableView: tableViewId,
                dismissedFilter: currentSelectedFilter,
            }));
            
            // Reset checked values here because we're programmatically unselecting
            // whatever the user had selected
            setCheckedValues(s => ({ ...s, viewFilter: ["All"] }));
            
            setOpen(false);
            resetFocus("FilterButtonClicked");
        }
        else if (!open) {
            logUsage("FilterMenuExpand", getMailboxInfoFromTableViewId(tableViewId));
            setOpen(true);
        }
    }, [
        currentSelectedFilter,
        filterMenuSource,
        isUsingOverrideToSortBySize,
        mailFolderTableQuery?.scenarioType,
        open,
        tableViewId,
    ]);
    
    const onViewFilterClicked = React.useCallback((viewFilter: ViewFilter) => {
        return () => {
            lazySelectFilter.importAndExecute(viewFilter, filterMenuSource);
            
            setOpen(false);
            resetFocus("MailListViewFilterClicked");
            
            addMailListLog(getMailListLogObjectToAddToStore("ApplyListViewFilter", {
                tableView: tableViewId,
                filterApplied: viewFilter,
            }));
        };
    }, [filterMenuSource, tableViewId]);
    
    const menuItemFilterIconStyles = useMenuItemFilterIconStyles();
    const viewFilterMenuItems = useComputedValue(() => {
        const viewFiltersToDisplay: ViewFilter[] = supportedViewFilters.filter((viewFilter: ViewFilter) => shouldShowViewFilterForMailbox(viewFilter, mailboxInfo));
        
        if (prioritizeOnDeliveryOn) {
            viewFiltersToDisplay.splice(1, 0, "CopilotHighPriority");
        }
        
        return viewFiltersToDisplay.map((viewFilter: ViewFilter) => {
            const viewFilterDisplayText = getViewFilterDisplay(viewFilter);
            const onViewFilterClickedCallback = onViewFilterClicked(viewFilter);
            
            // Clone the icon element so we can update the classes that are applied
            // to it so that we can effectively resize the icon from Fluent based
            // on density (required to unset font-size as it's applied directly
            // as a style and has highest specificity)
            const iconElement = getViewFilterIcon(viewFilter);
            const styledIconElement = React.cloneElement(iconElement, {
                key: viewFilter,
                className: classnames(menuItemFilterIconStyles.icon),
                style: { fontSize: "unset" },
            });
            
            return (<MenuItemRadio key={viewFilter} className={viewFilterMenuOption} icon={styledIconElement} name="viewFilter" onClick={onViewFilterClickedCallback} title={viewFilterDisplayText} value={viewFilter}>
                        <span className={getDensityModeCssClass(filterMenuItemLabelFull, filterMenuItemLabelMediumOrCompact, filterMenuItemLabelMediumOrCompact)}>
                            {viewFilterDisplayText}
                        </span>
                    </MenuItemRadio>);
        });
    }, [
        currentSelectedFilter,
        filterMenuSource,
        mailboxInfo,
        supportedViewFilters,
        tableViewId,
        menuItemFilterIconStyles,
        prioritizeOnDeliveryOn,
    ]);
    
    const filterButtonIconStyles = useFilterButtonIconStyles();
    const { filled, iconColorClassName, onMouseEnter, onMouseLeave } = useIconFillOnHover();
    const iconElement = currentSelectedFilter == "All" ? (<FilterIcon filled={filled}/>) : (getViewFilterIcon(currentSelectedFilter));
    const filterButtonIconElement = React.cloneElement(iconElement, {
        className: mergeClasses(isRPDenselessEnabled
            ? filterButtonIconStyles.full
            : getDensityModeCssClass(filterButtonIconStyles.full, filterButtonIconStyles.mediumOrCompact, filterButtonIconStyles.mediumOrCompact), iconColorClassName),
    });
    
    const menuButton = (<MenuButton icon={filterButtonIconElement} onClick={onFilterButtonClicked} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className={classnames(open ? filterButtonOpened : filterButton, isRPDenselessEnabled
            ? showFilterButtonLabel
                ? filterButtonDenseless
                : filterButtonDenselessIconOnly
            : getDensityModeCssClass(isSingleLineListView() ? filterButtonFullMCL : filterButtonFull, isSingleLineListView() ? filterButtonMediumMCL : filterButtonMedium, isSingleLineListView() ? filterButtonCompactMCL : filterButtonCompact), filterButtonClassName, mergeStyles({
            color: `${getPalette().neutralDark}`,
        }), isBleedThrough && bleedThroughBackground)} menuIcon={null} id="mailListFilterMenu">
                {showFilterButtonLabel && filterMenuLabel}
            </MenuButton>);
    
    // If the filter button is being used to display the label, then we don't want
    // to wrap it in a Tooltip. Otherwise, we do so that we can display the label
    // when the user hovers over the button.
    const wrappedMenuButton = showFilterButtonLabel ? (menuButton) : (<Tooltip content={filterMenuLabel} relationship="label">
                {menuButton}
            </Tooltip>);
    
    return (<Menu hasIcons={true} // Use this property if any of the items in the menu has an icon so items are aligned
     open={open && isListViewShown} onOpenChange={onOpenChange} checkedValues={checkedValues} onCheckedValueChange={onCheckedValueChange} positioning={menuPositioning}>
                <MenuTrigger disableButtonEnhancement={true}>{wrappedMenuButton}</MenuTrigger>
                <MenuPopover>
                    <MenuList>{viewFilterMenuItems}</MenuList>
                </MenuPopover>
            </Menu>);
}, "MailListFilterMenu");
export default defaultExport;
